import { TinyColor } from '@ctrl/tinycolor';
import generatePalette from '~utils/generate-palette';
import {
    BLUE_200,
    BLUE_300,
    BLUE_400,
    BLUE_500,
    BLUE_600,
    BLUE_700,
    BLUE_800,
    BLUE_900,
    CAMPAIGN_CHART_COLORS,
    ORANGE_500,
    ORANGE_600,
    ORANGE_700,
    ORANGE_800,
    ORANGE_900,
    PINK_300,
    PINK_400,
    PINK_600,
    PINK_700,
    PINK_800,
    PINK_900,
    REGION_COLORS,
    UNKNOWN_CT_BLUE,
} from '~styles/colors';
import type { KpiPalette, KpiThemePalette } from '../types';

type SeriesColorKind = 'fill' | 'line';

interface PaletteTheme {
    fill: string[];
    /** Blue and pink stop at tones 5 and 6, past which a line is too pale. */
    line: string[];
    anchor: string;
}

const ORANGE_RAMP = [ORANGE_800, ORANGE_700, ORANGE_600, ORANGE_500];
const BROWN_RAMP = [ORANGE_900, ...ORANGE_RAMP];

const PALETTE_THEMES: Record<KpiThemePalette, PaletteTheme> = {
    blue: {
        fill: [
            BLUE_900,
            BLUE_800,
            BLUE_700,
            BLUE_600,
            BLUE_500,
            BLUE_400,
            BLUE_300,
            BLUE_200,
        ],
        line: [BLUE_900, BLUE_800, BLUE_700, BLUE_600, BLUE_500],
        anchor: BLUE_700,
    },
    pink: {
        fill: [PINK_900, PINK_800, PINK_700, PINK_600, PINK_400, PINK_300],
        line: [PINK_900, PINK_800, PINK_700, PINK_600],
        anchor: PINK_700,
    },
    orange: { fill: ORANGE_RAMP, line: ORANGE_RAMP, anchor: ORANGE_800 },
    brown: { fill: BROWN_RAMP, line: BROWN_RAMP, anchor: ORANGE_900 },
};

const REGION_COLORS_BY_LOWER_LABEL = new Map(
    Object.entries(REGION_COLORS).map(([label, color]) => [
        label.toLowerCase(),
        color,
    ])
);

export const isThemePalette = (
    palette?: KpiPalette
): palette is KpiThemePalette =>
    palette !== undefined &&
    Object.prototype.hasOwnProperty.call(PALETTE_THEMES, palette);

const sampleRamp = (ramp: string[], position: number): string => {
    const lower = Math.floor(position);
    const fraction = position - lower;
    return fraction === 0
        ? ramp[lower]
        : new TinyColor(ramp[lower])
              .mix(ramp[lower + 1], fraction * 100)
              .toHexString()
              .toUpperCase();
};

const spreadOverRamp = (ramp: string[], count: number): string[] =>
    Array.from({ length: count }, (_, index) => {
        const position = (index * (ramp.length - 1)) / (count - 1);
        return count > ramp.length
            ? sampleRamp(ramp, position)
            : ramp[Math.round(position)];
    });

const resolveThemeColors = (
    count: number,
    theme: KpiThemePalette,
    kind: SeriesColorKind
): string[] => {
    const { anchor, [kind]: ramp } = PALETTE_THEMES[theme];
    return count === 1 ? [anchor] : spreadOverRamp(ramp, count);
};

const resolvePositionalColors = (
    count: number,
    palette: KpiPalette | undefined,
    kind: SeriesColorKind
): string[] | undefined => {
    if (isThemePalette(palette)) {
        return resolveThemeColors(count, palette, kind);
    }
    if (palette === 'campaign') {
        return generatePalette(CAMPAIGN_CHART_COLORS, count);
    }
    return undefined;
};

/** One colour per series: under a theme, business-unit labels take their fixed
 * colour and the rest spread over the `kind` ramp (the anchor for one);
 * `campaign` by position; else `undefined`, so the form keeps its default. */
export const resolveSeriesColors = (
    labels: string[],
    palette: KpiPalette | undefined,
    kind: SeriesColorKind
): string[] | undefined => {
    if (!isThemePalette(palette)) {
        return resolvePositionalColors(labels.length, palette, kind);
    }
    const regionColors = labels.map((label) =>
        REGION_COLORS_BY_LOWER_LABEL.get(label.toLowerCase())
    );
    const themeColors = resolveThemeColors(
        regionColors.filter((color) => color === undefined).length,
        palette,
        kind
    );
    let nextThemeColor = 0;
    return regionColors.map((color) => color ?? themeColors[nextThemeColor++]);
};

/** One colour per series, slice or bar, by position; `undefined` when the
 * palette has no positional ramp, so the form keeps its own default. */
export const resolvePaletteColors = (
    count: number,
    palette?: KpiPalette
): string[] | undefined => resolvePositionalColors(count, palette, 'fill');

/** A one-series form's colour: the theme's anchor or the palette's first, CT
 * blue without one. */
export const resolveSingleSeriesColor = (palette?: KpiPalette): string =>
    resolvePaletteColors(1, palette)?.[0] ?? UNKNOWN_CT_BLUE;
